import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import FutureBoardProgress from "./FutureBoardProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  POLISH_FIELDS, assistBlocker, assistFailureKey, assistReducer, buildAssistRequest, fieldForMode, formatDateTime,
  getFutureAssistJob, getFutureAssistQuota, initialAssistState, modeForStep, parseFutureBoardError, requestFutureAssist, runAssist,
  type AssistDraft, type AssistField, type AssistMode, type AssistQuota,
} from "@chronospark/shared";

type Translate = (key: string, options?: object) => string;

interface Props {
  step: number;
  cardId: number;
  draft: AssistDraft;
  accessToken?: string;
  language: string;
  t: Translate;
  /** 「使う」：その項目の入力欄に入れる（保存はしない） */
  onApply: (field: AssistField, text: string) => void;
  /** 項目のある段階（候補を入れた先を見に行く） */
  stepOfField: (field: AssistField) => number;
  onGoto: (step: number) => void;
}

const FIELD_LABEL: Record<AssistField, string> = {
  title: "futureBoard.edit.titleLabel", meaning: "futureBoard.edit.meaningLabel",
  self_image: "futureBoard.edit.selfImageLabel", scene: "futureBoard.edit.sceneLabel",
};

// 「ヒントをもらう」。本人が押したときだけ動く。候補は「使う」を押すと入力欄に入るだけで、保存は別の操作。
// 使えないとき（機能が無効・読み込みの失敗）は、何も表示しない（手入力は、そのまま使える）。
function AssistPanel({ step, cardId, draft, accessToken, language, t, onApply, stepOfField, onGoto }: Props) {
  const [quota, setQuota] = useState<AssistQuota | null>(null);
  const [hidden, setHidden] = useState(false);
  const [state, dispatch] = useReducer(assistReducer, initialAssistState);
  const [polishField, setPolishField] = useState<AssistField>("self_image");
  const [answerText, setAnswerText] = useState("");
  const cancelled = useRef(false);
  const mode: AssistMode = modeForStep(step);

  const loadQuota = useCallback(async () => {
    if (!accessToken) return;
    try {
      const result = await getFutureAssistQuota(accessToken);
      if (!result.enabled) setHidden(true);
      else setQuota(result);
    } catch {
      setHidden(true); // 無効（503）も、通信の失敗も、何も出さない
    }
  }, [accessToken]);

  useEffect(() => {
    void loadQuota();
  }, [loadQuota]);

  // 段階を移ったら、前の段階のヒントは閉じる（古い候補を、別の段階で使わない）
  useEffect(() => {
    cancelled.current = true;
    dispatch({ type: "close" });
    setAnswerText("");
  }, [step]);

  const field = fieldForMode(mode, polishField);
  const blocker = assistBlocker(mode, draft, field);
  const exhausted = quota !== null && (quota.remaining ?? 0) <= 0;
  const zone = quota?.timezone ?? "Asia/Tokyo";
  const timeLabel = quota?.resets_at ? formatDateTime(quota.resets_at, language, zone) : "";

  const start = useCallback(async (answers: Array<{ question: string; answer: string }> = state.answers) => {
    if (!accessToken) return;
    cancelled.current = false;
    dispatch({ type: "start", mode, field });
    const body = buildAssistRequest({ mode, language, cardId, draft, field, answers });
    try {
      const outcome = await runAssist(body, {
        submit: payload => requestFutureAssist(accessToken, payload),
        poll: jobId => getFutureAssistJob(accessToken, jobId),
        isCancelled: () => cancelled.current,
      });
      if (outcome.outcome === "cancelled") return;
      if (outcome.outcome === "result") dispatch({ type: "result", result: outcome.result });
      else dispatch({ type: "failed", messageKey: assistFailureKey(outcome.outcome === "failed" ? outcome.code : "AI_TIMEOUT") });
    } catch (error) {
      const failure = parseFutureBoardError(error);
      if (failure.kind === "ai_disabled") {
        setHidden(true);
        return;
      }
      dispatch({ type: "failed", messageKey: failure.messageKey, waitSeconds: failure.retryAfterSeconds ?? null, resetsAt: failure.resetsAt ?? null });
    } finally {
      void loadQuota(); // 残りの回数を、最新にする
    }
  }, [accessToken, mode, field, language, cardId, draft, state.answers, loadQuota]);

  const sendAnswer = () => {
    const text = answerText.trim();
    const answers = text && state.question ? [...state.answers, { question: state.question, answer: text }] : state.answers;
    if (text) dispatch({ type: "answer", text });
    setAnswerText("");
    void start(answers);
  };

  const close = () => {
    cancelled.current = true;
    dispatch({ type: "close" });
    setAnswerText("");
  };

  if (hidden || !quota) return null;

  const working = state.phase === "working";
  const startDisabled = working || exhausted || blocker !== null;
  const errorText = !state.messageKey ? ""
    : state.resetsAt ? t("futureBoard.assist.exhausted", { time: formatDateTime(state.resetsAt, language, zone) })
    : state.waitSeconds ? `${t(state.messageKey)} ${t("futureBoard.assist.waitSeconds", { seconds: state.waitSeconds })}`
    : t(state.messageKey);

  return (
    <Card variant="outlined" sx={{ mt: 3, p: 2, bgcolor: "grey.50" }} component="section" aria-label={t("futureBoard.assist.title")}>
      <Typography variant="subtitle1" component="h3">{t("futureBoard.assist.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t("futureBoard.assist.intro")}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>{t("futureBoard.assist.privacy")}</Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 1.5 }} aria-live="polite">
        {exhausted
          ? t("futureBoard.assist.exhausted", { time: timeLabel })
          : t("futureBoard.assist.remaining", { remaining: quota.remaining ?? 0, limit: quota.daily_limit ?? 0 })}
      </Typography>

      {(state.phase === "idle" || state.phase === "error" || state.phase === "none") && (
        <Stack spacing={1.5}>
          {mode === "polish" && (
            <FormControl size="small" sx={{ width: { xs: "100%", sm: 360 }, maxWidth: "100%" }}>
              <InputLabel id="assist-field-label">{t("futureBoard.assist.polishField")}</InputLabel>
              <Select labelId="assist-field-label" label={t("futureBoard.assist.polishField")} value={polishField} onChange={event => setPolishField(event.target.value as AssistField)}>
                {POLISH_FIELDS.map(item => <MenuItem key={item} value={item}>{t(FIELD_LABEL[item])}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {blocker && <Typography variant="body2" color="text.secondary">{t(blocker)}</Typography>}
          <Box>
            <Button variant="contained" onClick={() => void start()} disabled={startDisabled} startIcon={<span aria-hidden>✨</span>}>
              {t(`futureBoard.assist.start.${mode}`)}
            </Button>
          </Box>
        </Stack>
      )}

      {working && (
        <Stack direction="row" spacing={1.5} alignItems="center" role="status">
          <FutureBoardProgress size={20} />
          <Typography variant="body2">{t("futureBoard.assist.working")}</Typography>
          <Button size="small" onClick={close}>{t("futureBoard.assist.cancel")}</Button>
        </Stack>
      )}

      {state.phase === "error" && state.messageKey && (
        <Alert severity="info" sx={{ mt: 1.5 }} role="alert">
          {errorText}
        </Alert>
      )}

      {state.phase === "none" && (
        <Alert severity="info" sx={{ mt: 1.5 }}>{t("futureBoard.assist.none")}</Alert>
      )}

      {state.phase === "question" && state.question && (
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">{t("futureBoard.assist.questionLabel")}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>{state.question}</Typography>
          </Box>
          <TextField
            multiline minRows={2} size="small" label={t("futureBoard.assist.answerLabel")} value={answerText}
            onChange={event => setAnswerText(event.target.value)} inputProps={{ maxLength: 600 }}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="contained" onClick={sendAnswer} disabled={exhausted || working}>
              {t("futureBoard.assist.sendAnswer")}
            </Button>
            <Button onClick={() => void start()} disabled={exhausted || working}>{t("futureBoard.assist.newQuestion")}</Button>
            <Button onClick={close}>{t("futureBoard.assist.finish")}</Button>
          </Stack>
        </Stack>
      )}

      {state.phase === "candidates" && state.field && (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" component="h4">{t("futureBoard.assist.candidatesFor", { field: t(FIELD_LABEL[state.field]) })}</Typography>
          <Typography variant="caption" color="text.secondary">{t("futureBoard.assist.candidatesHeading")}</Typography>
          {state.imagined && <Typography variant="caption" color="warning.main">{t("futureBoard.assist.imagined")}</Typography>}
          {state.candidates.map((candidate, index) => (
            <Card key={`${index}-${candidate}`} variant="outlined" sx={{ p: 1.5, bgcolor: "background.paper" }}>
              <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mb: 1 }}>{candidate}</Typography>
              {state.used.includes(index) ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="success.main">{t("futureBoard.assist.used")}</Typography>
                  {stepOfField(state.field as AssistField) !== step && (
                    <Button size="small" onClick={() => onGoto(stepOfField(state.field as AssistField))}>{t(FIELD_LABEL[state.field as AssistField])}</Button>
                  )}
                </Stack>
              ) : (
                <Button size="small" variant="outlined" onClick={() => { onApply(state.field as AssistField, candidate); dispatch({ type: "use", index }); }}>
                  {t("futureBoard.assist.use")}
                </Button>
              )}
            </Card>
          ))}
          <Stack direction="row" spacing={1}>
            <Button onClick={() => void start()} disabled={exhausted || working}>{t("futureBoard.assist.again")}</Button>
            <Button onClick={close}>{t("futureBoard.assist.close")}</Button>
          </Stack>
        </Stack>
      )}
    </Card>
  );
}

export default AssistPanel;
